import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

// Shared styles
const thStyle = {
  padding: '2mm 3mm',
  color: '#fff',
  fontWeight: 'bold',
  textAlign: 'center',
  border: '1px solid #1a365d'
};

const tdStyle = {
  padding: '2mm 3mm',
  textAlign: 'center',
  border: '1px solid #e2e8f0'
};

// Premium Government-Style Marksheet Component
const PremiumMarksheet = forwardRef(({ marksheet, certificate, settings }, ref) => {
  if (!marksheet) return null;

  const IQRA_LOGO = settings?.iqra_logo_url || 'https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/atupje9x_1%20copy%20-%20Copy.png';
  const SKILL_INDIA_LOGO = settings?.skill_india_logo_url || 'https://customer-assets.emergentagent.com/job_2f370b5d-868a-4240-92b5-92a3302f8c5a/artifacts/iym3ivrf_Untitled-1%201.png';
  const WATERMARK_LOGO = settings?.watermark_url || SKILL_INDIA_LOGO;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const marksheetTitle = marksheet.marksheet_type === 'RPL' 
    ? 'Recognition of Prior Learning - Statement of Marks'
    : 'Short Term Training - Statement of Marks';

  const verificationUrl = settings?.verification_base_url 
    ? `${settings.verification_base_url}/verify/${encodeURIComponent(certificate?.certificate_number || marksheet.marksheet_number)}`
    : `${window.location.origin}/verify/${encodeURIComponent(certificate?.certificate_number || marksheet.marksheet_number)}`;

  // Get marks data from marks_data or subjects array
  const allMarks = marksheet.marks_data || marksheet.subjects || [];
  
  // If still empty, create from marksheet data
  const displayMarks = allMarks.length > 0 ? allMarks : [
    {
      module_code: 'EXAM-001',
      module_name: marksheet.course_name || certificate?.course_name || 'Examination',
      type: 'Core',
      max_marks: marksheet.max_marks || 100,
      obtained_marks: marksheet.total_marks || 0
    }
  ];
  
  // Group marks by type
  const coreMarks = displayMarks.filter(m => m.type === 'Core' || !m.type) || [];
  const nonCoreMarks = displayMarks.filter(m => m.type === 'Non-Core') || [];

  return (
    <div 
      ref={ref}
      className="marksheet-container"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '10mm',
        background: '#fff',
        position: 'relative',
        fontFamily: "'Times New Roman', serif",
        boxSizing: 'border-box'
      }}
    >
      {/* Decorative Border */}
      <div style={{
        position: 'absolute',
        top: '5mm',
        left: '5mm',
        right: '5mm',
        bottom: '5mm',
        border: '3px double #1a365d',
        borderRadius: '5px',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '2mm',
          left: '2mm',
          right: '2mm',
          bottom: '2mm',
          border: '1px solid #D4AF37',
          borderRadius: '3px'
        }} />
      </div>

      {/* Watermark */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0.05,
        pointerEvents: 'none',
        zIndex: 0
      }}>
        <img 
          src={WATERMARK_LOGO} 
          alt="" 
          style={{ width: '120mm', height: 'auto' }} 
          crossOrigin="anonymous"
        />
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, padding: '10mm 15mm' }}>
        {/* Header with Logos */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '6mm'
        }}>
          {/* Left Logo - IQRA Skills */}
          <div style={{ textAlign: 'center', width: '50mm' }}>
            <img 
              src={IQRA_LOGO} 
              alt="IQRA Skills" 
              style={{ height: '30mm', objectFit: 'contain' }} 
            />
          </div>

          {/* Center Title - MARKSHEET */}
          <div style={{ textAlign: 'center', flex: 1, margin: '0 10mm' }}>
            <h1 style={{
              fontSize: '32px',
              color: '#1a365d',
              fontWeight: 'bold',
              letterSpacing: '4px',
              textTransform: 'uppercase',
              margin: 0
            }}>
              MARKSHEET
            </h1>
          </div>

          {/* Right Logo - Skill India */}
          <div style={{ textAlign: 'center', width: '50mm' }}>
            <img 
              src={SKILL_INDIA_LOGO} 
              alt="Skill India" 
              style={{ height: '35mm', objectFit: 'contain' }} 
            />
          </div>
        </div>

        {/* Marksheet Title */}
        <div style={{ textAlign: 'center', marginBottom: '6mm' }}>
          <div style={{
            display: 'inline-block',
            padding: '2mm 12mm',
            background: 'linear-gradient(135deg, #1a365d, #2c5282)',
            borderRadius: '3px'
          }}>
            <h1 style={{
              fontSize: '18px',
              color: '#fff',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0
            }}>
              STATEMENT OF MARKS
            </h1>
          </div>
          <p style={{
            fontSize: '11px',
            color: '#4a5568',
            marginTop: '2mm',
            fontStyle: 'italic'
          }}>
            {marksheetTitle}
          </p>
          <p style={{
            fontSize: '9px',
            color: '#718096',
            marginTop: '1mm'
          }}>
            Marksheet No: <strong>{marksheet.marksheet_number}</strong>
          </p>
        </div>

        {/* Student Details */}
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '3px',
          padding: '4mm',
          marginBottom: '5mm'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2mm 10mm',
            fontSize: '10px'
          }}>
            <div><strong>Student Name:</strong> {marksheet.student_name}</div>
            <div><strong>Enrollment No:</strong> {marksheet.enrollment_number}</div>
            <div><strong>Course/Qualification:</strong> {marksheet.course_name}</div>
            <div><strong>Assessment Date:</strong> {formatDate(marksheet.assessment_date)}</div>
            {marksheet.aadhaar_number && (
              <div><strong>Aadhaar:</strong> {marksheet.aadhaar_number}</div>
            )}
          </div>
        </div>

        {/* Marks Table */}
        <div style={{ marginBottom: '5mm' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '9px'
          }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #1a365d, #2c5282)' }}>
                <th style={{ ...thStyle, width: '15%' }}>Subject Code</th>
                <th style={{ ...thStyle, width: '45%' }}>Subject Title</th>
                <th style={{ ...thStyle, width: '12%' }}>Type</th>
                <th style={{ ...thStyle, width: '14%' }}>Max Marks</th>
                <th style={{ ...thStyle, width: '14%' }}>Obtained</th>
              </tr>
            </thead>
            <tbody>
              {/* Core Section Header */}
              {coreMarks.length > 0 && (
                <tr style={{ background: '#e6f2ff' }}>
                  <td colSpan={5} style={{ ...tdStyle, fontWeight: 'bold', color: '#1a365d' }}>
                    Core / Technical Subjects
                  </td>
                </tr>
              )}
              {coreMarks.slice(0, 10).map((mark, idx) => (
                <tr key={`core-${idx}`} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={tdStyle}>{mark.module_code}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>{mark.module_name}</td>
                  <td style={tdStyle}>{mark.type}</td>
                  <td style={tdStyle}>{mark.max_marks}</td>
                  <td style={{ ...tdStyle, fontWeight: 'bold', color: mark.obtained_marks > 0 ? '#059669' : '#dc2626' }}>
                    {mark.obtained_marks}
                  </td>
                </tr>
              ))}
              
              {/* Core Subtotal */}
              {coreMarks.length > 0 && (
                <tr style={{ background: '#e6f2ff', fontWeight: 'bold' }}>
                  <td colSpan={3} style={{ ...tdStyle, textAlign: 'right' }}>Core Total:</td>
                  <td style={tdStyle}>{marksheet.core_max}</td>
                  <td style={{ ...tdStyle, color: '#1a365d' }}>{marksheet.core_total}</td>
                </tr>
              )}

              {/* Non-Core Section */}
              {nonCoreMarks.length > 0 && (
                <>
                  <tr style={{ background: '#fef3c7' }}>
                    <td colSpan={5} style={{ ...tdStyle, fontWeight: 'bold', color: '#92400e' }}>
                      Non-Core / Soft Skills Subjects
                    </td>
                  </tr>
                  {nonCoreMarks.slice(0, 5).map((mark, idx) => (
                    <tr key={`noncore-${idx}`} style={{ background: idx % 2 === 0 ? '#fff' : '#fffbeb' }}>
                      <td style={tdStyle}>{mark.module_code}</td>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>{mark.module_name}</td>
                      <td style={tdStyle}>{mark.type}</td>
                      <td style={tdStyle}>{mark.max_marks}</td>
                      <td style={{ ...tdStyle, fontWeight: 'bold', color: mark.obtained_marks > 0 ? '#059669' : '#dc2626' }}>
                        {mark.obtained_marks}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#fef3c7', fontWeight: 'bold' }}>
                    <td colSpan={3} style={{ ...tdStyle, textAlign: 'right' }}>Non-Core Total:</td>
                    <td style={tdStyle}>{marksheet.non_core_max}</td>
                    <td style={{ ...tdStyle, color: '#92400e' }}>{marksheet.non_core_total}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '3mm',
          marginBottom: '6mm'
        }}>
          <SummaryBox label="Total Marks" value={`${marksheet.overall_total} / ${marksheet.overall_max}`} color="#1a365d" />
          <SummaryBox label="Percentage" value={`${marksheet.percentage?.toFixed(1)}%`} color="#0369a1" />
          <SummaryBox label="Grade" value={marksheet.grade} color={marksheet.grade === 'A+' ? '#059669' : '#1a365d'} />
          <SummaryBox label="Result" value={marksheet.result} color={marksheet.result === 'PASS' ? '#059669' : '#dc2626'} />
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginTop: '8mm',
          paddingTop: '4mm',
          borderTop: '1px dashed #D4AF37'
        }}>
          {/* QR Code */}
          <div style={{ textAlign: 'center' }}>
            <QRCodeSVG 
              value={verificationUrl}
              size={60}
              level="M"
              includeMargin={true}
              style={{ border: '1px solid #D4AF37', borderRadius: '3px', padding: '1mm' }}
            />
            <p style={{ fontSize: '6px', color: '#718096', marginTop: '1mm', maxWidth: '25mm' }}>
              Scan to verify
            </p>
          </div>

          {/* Seal */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '28mm',
              height: '28mm',
              borderRadius: '50%',
              border: '2px double #D4AF37',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)'
            }}>
              <div style={{
                fontSize: '7px',
                color: '#D4AF37',
                fontWeight: 'bold',
                textAlign: 'center',
                transform: 'rotate(-10deg)'
              }}>
                VERIFIED<br/>✓
              </div>
            </div>
          </div>

          {/* Signatory */}
          <div style={{ textAlign: 'center', minWidth: '45mm' }}>
            {settings?.signatory_signature_url && (
              <div style={{ 
                padding: '2mm 3mm', 
                display: 'inline-block',
                marginBottom: '1mm'
              }}>
                <img 
                  src={settings.signatory_signature_url} 
                  alt="Signature" 
                  style={{ height: '10mm', objectFit: 'contain' }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
            <div style={{ borderTop: '1px solid #1a365d', paddingTop: '1mm' }}>
              <p style={{ fontSize: '10px', fontWeight: 'bold', color: '#1a365d', margin: 0 }}>
                {settings?.signatory_name || 'Director'}
              </p>
              <p style={{ fontSize: '8px', color: '#4a5568', margin: 0 }}>
                {settings?.signatory_designation || 'IQRA Skills Pvt Ltd'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Summary Box Component
const SummaryBox = ({ label, value, color }) => (
  <div style={{
    background: '#f8fafc',
    border: `2px solid ${color}`,
    borderRadius: '5px',
    padding: '3mm',
    textAlign: 'center'
  }}>
    <p style={{ fontSize: '8px', color: '#64748b', margin: 0, marginBottom: '1mm' }}>{label}</p>
    <p style={{ fontSize: '14px', fontWeight: 'bold', color, margin: 0 }}>{value}</p>
  </div>
);

PremiumMarksheet.displayName = 'PremiumMarksheet';

export default PremiumMarksheet;
